import React, { useEffect, useState, useMemo, useCallback } from "react";
import DashboardService from "@/services/api/dashboard";
import Table from "@/components/Table/Table";
import { Tooltip } from "@mui/material";
import {
    Card,
    CardContent,
    Typography,
    Grid,
    Box,
    Chip,
    Button,
    Skeleton,
    IconButton,
    useTheme
} from "@mui/material";
import {
    TrendingUp,
    ShoppingCart,
    Store,
    ArrowUpward,
    ArrowDownward,
    NotificationsActive,
    Warning,
    Refresh
} from "@mui/icons-material";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Legend,
    Filler,
    Tooltip as ChartTooltip,
    type ChartOptions
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { useNavigate } from "react-router-dom";
import { renderStatus } from "../Order/component/OrderColumn";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

// --- Types (Ideally move to src/types/dashboard.ts) ---
interface Metric {
    value: number;
    trend: number;
    trendDirection: "up" | "down" | "neutral";
    label: string;
}

interface MetricsData {
    revenue: Metric;
    orders: Metric;
    avgOrderValue: Metric;
    conversionRate: Metric;
}

interface SalesOverviewItem {
    label: string;
    value: number;
}

interface OrdersSummaryItem {
    label: string;
    deliveredOrders: number;
    pendingOrders: number;
}

interface InventoryAlert {
    productName: string;
    sku: string;
    stock: number;
    alertType: string;
    urgent: boolean;
}

interface RecentOrder {
    orderId: string;
    customer: string;
    date: string;
    status: string;
    total: number;
}

interface LowStockProduct {
    productName: string;
    sku: string;
    quantity: number;
}

interface DashboardData {
    metrics: MetricsData;
    salesOverview: SalesOverviewItem[];
    ordersSummary: OrdersSummaryItem[];
    inventoryAlerts: InventoryAlert[];
    recentOrders: RecentOrder[];
    lowStockProducts: LowStockProduct[];
}

// --- Props ---
interface MetricCardProps {
    title: string;
    value: string | number;
    trend: number;
    trendLabel: string;
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
}

// --- Components ---

const MetricCard: React.FC<MetricCardProps> = React.memo(({ title, value, trend, trendLabel, icon, iconBg, iconColor }) => (
    <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3, xl: 3 }}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%', position: 'relative', overflow: 'visible', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
            <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                    <Box
                        sx={{
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            bgcolor: iconBg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: iconColor,
                            mr: 2,
                            boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                        }}
                    >
                        {icon}
                    </Box>
                    <Box>
                        <Typography variant="body2" color="textSecondary" fontWeight={500}>{title}</Typography>
                        <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5 }}>{value}</Typography>
                    </Box>
                </Box>
                <Box display="flex" alignItems="center">
                    {trend !== 0 && (
                        <Chip
                            label={`${trend > 0 ? '+' : ''}${trend}%`}
                            size="small"
                            color={trend > 0 ? "success" : "error"}
                            variant="filled"
                            sx={{
                                bgcolor: trend > 0 ? '#e8f5e9' : '#ffebee',
                                color: trend > 0 ? '#2e7d32' : '#c62828',
                                fontWeight: 'bold',
                                height: 24,
                                mr: 1,
                                '& .MuiChip-label': { px: 1 }
                            }}
                            icon={trend > 0 ? <ArrowUpward style={{ width: 14 }} /> : <ArrowDownward style={{ width: 14 }} />}
                        />
                    )}
                    <Typography variant="caption" color="textSecondary">{trendLabel}</Typography>
                </Box>
            </CardContent>
        </Card>
    </Grid>
));

const LoadingSkeleton = () => (
    <Grid container spacing={3}>
        {[1, 2, 3, 4].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 3 }} />
            </Grid>
        ))}
        <Grid size={{ xs: 12, md: 8 }}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
        </Grid>
    </Grid>
);

const Dashboard: React.FC = () => {
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    // Lazy read currency
    const [currency] = useState(() => localStorage.getItem("currency") || "$");
    const theme = useTheme();

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        // AbortController could be added here if service supports it
        try {
            const res = await DashboardService.GetSupplierDashboardData();
            if (res && res.data) {
                setData(res.data);
            } else {
                throw new Error("No data received");
            }
        } catch (err) {
            console.error("Failed to fetch dashboard data", err);
            setError("Failed to load dashboard data. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- Memoized Chart Options ---
    const lineChartOptions = useMemo<ChartOptions<'line'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                titleColor: '#1a237e',
                bodyColor: '#333',
                borderColor: '#e0e0e0',
                borderWidth: 1,
                padding: 10,
                displayColors: false,
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: '#f0f0f0', tickBorderDash: [5, 5] },
                ticks: { color: '#757575', font: { size: 11 } }
            },
            x: {
                grid: { display: false },
                ticks: { color: '#757575', font: { size: 11 } }
            }
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
    }), []);

    const barChartOptions = useMemo<ChartOptions<'bar'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: { usePointStyle: true, boxWidth: 8, font: { size: 12 } }
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: '#f0f0f0' },
                ticks: { font: { size: 11 } }
            },
            x: {
                grid: { display: false },
                ticks: { font: { size: 11 } }
            }
        }
    }), []);

    // --- Table Columns ---
    const recentOrdersColumns = useMemo(() => [
        { name: "Order ID", selector: (row: RecentOrder) => row.orderId, sortable: true, grow: 1 },
        { name: "Customer", selector: (row: RecentOrder) => row.customer, sortable: true, grow: 1.5 },
        { name: "Date", selector: (row: RecentOrder) => row.date, sortable: true },
        {
            name: "Status",
            selector: (row: RecentOrder) => renderStatus(row.status),
            cell: (row: RecentOrder) => (
                <Chip
                    label={row.status}
                    size="small"
                    variant="filled"
                    sx={{
                        bgcolor: row.status === 'Delivered' ? '#e8f5e9' : '#fff3e0',
                        color: row.status === 'Delivered' ? '#2e7d32' : '#ef6c00',
                        fontWeight: 'bold',
                        fontSize: '0.75rem'
                    }}
                />
            ),
            sortable: true
        },
        { name: "Total", selector: (row: RecentOrder) => `${currency} ${row.total.toFixed(2)}`, sortable: true, right: true },
    ], [currency]);

    const lowStockColumns = useMemo(() => [
        { name: "Product", selector: (row: LowStockProduct) => row.productName, sortable: true, grow: 2 },
        { name: "SKU", selector: (row: LowStockProduct) => row.sku, sortable: true },
        {
            name: "Qty",
            selector: (row: LowStockProduct) => row.quantity,
            cell: (row: LowStockProduct) => (
                <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={row.quantity <= 10 ? "error" : "textPrimary"}
                >
                    {row.quantity}
                </Typography>
            ),
            sortable: true,
            right: true
        },
    ], []);

    if (loading) return (
        <div className="p-6 bg-gray-50 min-h-screen w-full">
            <div className="flex justify-between items-center mb-6">
                <Skeleton width={200} height={40} />
                <Skeleton variant="circular" width={40} height={40} />
            </div>
            <LoadingSkeleton />
        </div>
    );

    if (error) return (
        <div className="p-6 flex flex-col items-center justify-center min-h-[50vh]">
            <Warning color="error" sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>{error}</Typography>
            <Button variant="contained" startIcon={<Refresh />} onClick={fetchData} sx={{ mt: 2 }}>
                Retry
            </Button>
        </div>
    );

    if (!data) return null; // Should not happen if error is handled

    return (
        <div className="p-6 bg-gray-50 min-h-screen w-full">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <Typography variant="h4" fontWeight="800" sx={{ color: '#1a237e', letterSpacing: '-0.5px' }}>
                        Dashboard
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Welcome back, here's what's happening today.
                    </Typography>
                </div>
                {/* <Box>
                    <IconButton sx={{ bgcolor: 'white', boxShadow: 1 }}>
                        <NotificationsActive sx={{ color: '#1a237e' }} />
                    </IconButton>
                </Box> */}
            </div>

            {/* Metrics Cards */}
            <Grid container spacing={3} mb={4}>
                <MetricCard
                    title="Revenue Today"
                    value={`${currency} ${data.metrics.revenue.value.toLocaleString()}`}
                    trend={data.metrics.revenue.trend}
                    trendLabel={data.metrics.revenue.label}
                    icon={<TrendingUp fontSize="large" />}
                    iconBg="#e8eaf6"
                    iconColor="#3949ab"
                />
                <MetricCard
                    title="Orders Today"
                    value={data.metrics.orders.value.toLocaleString()}
                    trend={data.metrics.orders.trend}
                    trendLabel={data.metrics.orders.label}
                    icon={<ShoppingCart fontSize="large" />}
                    iconBg="#f3e5f5"
                    iconColor="#8e24aa"
                />
                <MetricCard
                    title="Avg. Order Value"
                    value={`${currency} ${data.metrics.avgOrderValue.value.toFixed(2)}`}
                    trend={data.metrics.avgOrderValue.trend}
                    trendLabel={data.metrics.avgOrderValue.label}
                    icon={<Typography variant="h5" fontWeight="bold">$</Typography>}
                    iconBg="#fff8e1"
                    iconColor="#ff8f00"
                />
                <MetricCard
                    title="Conversion Rate"
                    value={`${data.metrics.conversionRate.value}%`}
                    trend={data.metrics.conversionRate.trend}
                    trendLabel={data.metrics.conversionRate.label}
                    icon={<Typography variant="h5" fontWeight="bold">%</Typography>}
                    iconBg="#e8f5e9"
                    iconColor="#2e7d32"
                />
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3} mb={4}>
                <Grid size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.02)', height: '100%', border: '1px solid #f0f0f0' }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <Typography variant="h6" fontWeight="bold">Sales Overview</Typography>
                                <Chip label="Last 7 Days" size="small" variant="outlined" />
                            </Box>
                            <Box height={300}>
                                <Line
                                    data={{
                                        labels: Array.isArray(data.salesOverview) ? data.salesOverview.map(item => item.label) : [],
                                        datasets: [{
                                            label: "Sales",
                                            data: Array.isArray(data.salesOverview) ? data.salesOverview.map(item => item.value) : [],
                                            borderColor: "#4d7cfe",
                                            backgroundColor: (context) => {
                                                const ctx = context.chart.ctx;
                                                const gradient = ctx.createLinearGradient(0, 0, 0, 300);
                                                gradient.addColorStop(0, "rgba(77, 124, 254, 0.3)");
                                                gradient.addColorStop(1, "rgba(77, 124, 254, 0.0)");
                                                return gradient;
                                            },
                                            fill: true,
                                            tension: 0.4,
                                            pointRadius: 4,
                                            pointHoverRadius: 6
                                        }]
                                    }}
                                    options={lineChartOptions}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 6, xl: 6 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.02)', height: '100%', border: '1px solid #f0f0f0' }}>
                        <CardContent>
                            <Typography variant="h6" fontWeight="bold" mb={3}>Orders Summary</Typography>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="h3" fontWeight="bold" color="primary" mr={2}>
                                    {Array.isArray(data.ordersSummary) ? data.ordersSummary.reduce((a, b) => a + b.pendingOrders + b.deliveredOrders, 0) : 0}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">Total Orders</Typography>
                            </Box>
                            <Box height={220} mt={2}>
                                <Bar
                                    data={{
                                        labels: Array.isArray(data.ordersSummary) ? data.ordersSummary.map(item => item.label) : [],
                                        datasets: [
                                            {
                                                label: "Pending",
                                                data: Array.isArray(data.ordersSummary) ? data.ordersSummary.map(item => item.pendingOrders) : [],
                                                backgroundColor: "#7986cb",
                                                borderRadius: 4
                                            },
                                            {
                                                label: "Delivered",
                                                data: Array.isArray(data.ordersSummary) ? data.ordersSummary.map(item => item.deliveredOrders) : [],
                                                backgroundColor: "#81c784",
                                                borderRadius: 4
                                            }
                                        ]
                                    }}
                                    options={barChartOptions}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 6, xl: 6 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.02)', height: '100%', border: '1px solid #f0f0f0' }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', color: '#d32f2f' }}>
                                    <Warning color="error" sx={{ mr: 1 }} /> Inventory Alerts
                                </Typography>
                                <Button size="small" variant="text" onClick={() => navigate("/distributor/Stock")}>View All</Button>
                            </Box>
                            <Box sx={{ maxHeight: 300, overflowY: 'auto', pr: 1 }}>
                                {Array.isArray(data.inventoryAlerts) && data.inventoryAlerts.length > 0 ? (
                                    data.inventoryAlerts.map((alert, index) => (
                                        <Box key={index} p={1.5} mb={1.5} sx={{ bgcolor: '#fff0f0', borderRadius: 2, borderLeft: '4px solid #ef5350' }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight="bold">{alert.productName}</Typography>
                                                    <Typography variant="caption" color="textSecondary">SKU: {alert.sku}</Typography>
                                                    <Typography variant="caption" display="block" color="error.main" fontWeight="bold" mt={0.5}>
                                                        {alert.stock} units left
                                                    </Typography>
                                                </Box>
                                                {alert.urgent && (
                                                    <Tooltip title="Urgent Restock">
                                                        <Warning fontSize="small" color="error" />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    ))
                                ) : (
                                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height={150} color="text.secondary">
                                        <Store sx={{ fontSize: 40, mb: 1, opacity: 0.2 }} />
                                        <Typography variant="body2">No alerts</Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tables Section */}
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, lg: 7 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.02)', border: '1px solid #f0f0f0' }}>
                        <CardContent sx={{ px:2  }}>
                            <Box p={0} pb={1} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6" fontWeight="bold">Recent Orders</Typography>
                                <Button size="small" onClick={() => navigate("/distributor/Order")}>View All</Button>
                            </Box>
                            <Table
                                columns={recentOrdersColumns}
                                data={Array.isArray(data.recentOrders) ? data.recentOrders : []}
                                isBackendPagination={false}
                                paginationProps={{ isPagination: false, totalCount: 0, pageSelected: 1, setPageSelected: () => { }, setRowsPerPageValue: () => { } }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, lg: 5 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.02)', border: '1px solid #f0f0f0' }}>
                        <CardContent sx={{ px:2  }}>
                            <Box p={0} pb={1} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6" fontWeight="bold">Low Stock</Typography>
                                <Button size="small" onClick={() => navigate("/distributor/Stock")}>View All</Button>
                            </Box>
                            <Table
                                columns={lowStockColumns}
                                data={Array.isArray(data.lowStockProducts) ? data.lowStockProducts : []}
                                isBackendPagination={false}
                                paginationProps={{ isPagination: false, totalCount: 0, pageSelected: 1, setPageSelected: () => { }, setRowsPerPageValue: () => { } }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </div>
    );
};

export default Dashboard;